import React from 'react';
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { PlayerStats, StatKey } from '../types/PlayerShiftTypes';

/** Minimal shape needed to render a stat box — satisfied by StatDefinition. */
interface StatColumn {
  key: string;
  label: string;
  fullName?: string;
}

interface PlayerStatsRowProps {
  stats: PlayerStats;
  /** The active sport's stats, in display order. */
  statDefs: ReadonlyArray<StatColumn>;
  activeFilters: Set<StatKey>;
  onFilterChange: (stat: StatKey) => void;
}

const StatBox: React.FC<{
  value: number;
  label: string;
  /** Full stat name; when present, shown as a hover tooltip over the box. */
  tooltip?: string;
  isActive: boolean;
  onClick: () => void;
}> = ({ value, label, tooltip, isActive, onClick }) => {
  const theme = useTheme();

  const box = (
    <Box
      onClick={onClick}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '14px 16px 18px 16px',
        borderRadius: 1,
        backgroundColor: isActive ? '#F3F2F2' : theme.palette.cx.bg.elevated2,
        cursor: 'pointer',
        border: '1px solid transparent',
        transition: 'background-color 0.15s',
        '&:hover': {
          backgroundColor: isActive ? '#F3F2F2' : theme.palette.cx.border.subtle,
        },
      }}
    >
      <Typography
        sx={{
          color: isActive ? '#09090C' : '#FFF',
          textAlign: 'center',
          fontFamily: 'Sohne',
          fontSize: '36px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '40px',
          mb: 1,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          color: isActive ? '#2F2F36' : theme.palette.cx.light[200],
          textAlign: 'center',
          fontFamily: 'Sohne',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '20px',
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  // Only attach a tooltip when the full name adds information beyond the label.
  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      {box}
    </Tooltip>
  ) : (
    box
  );
};

export const PlayerStatsRow: React.FC<PlayerStatsRowProps> = ({
  stats,
  statDefs,
  activeFilters,
  onFilterChange,
}) => {
  if (statDefs.length === 0) return null;
  return (
    <Stack direction="row" spacing={1} sx={{ px: 1.5, pb: 1 }}>
      {statDefs.map((def) => (
        <StatBox
          key={def.key}
          value={stats[def.key] ?? 0}
          label={def.label}
          tooltip={def.fullName && def.fullName !== def.label ? def.fullName : undefined}
          isActive={activeFilters.has(def.key)}
          onClick={() => onFilterChange(def.key)}
        />
      ))}
    </Stack>
  );
};
